import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoAddOutline,
  IoCreateOutline,
  IoOpenOutline,
  IoTrashOutline,
} from "react-icons/io5";
import {
  createField,
  deleteField,
  listFields,
  updateField,
} from "../../../api/fieldsApi";
import { uploadMedia } from "../../../api/mediaApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import SocialFormModal from "./SocialFormModal";
import {
  groupSocialFields,
  nextSocialIndex,
  socialFieldDefs,
  socialGroupToForm,
} from "./socialFieldUtils";
import { resolveSocialIconSrc, SOCIAL_PLATFORM_OPTIONS } from "./socialPlatforms";
import { useFooterSectionAdmin } from "./useFooterSectionAdmin";

async function persistFieldValue(field, value) {
  await updateField(field.id, {
    label: field.label,
    fieldType: field.fieldType,
    key: field.key,
    isRichText: false,
    isRequired: field.isRequired,
    isPublished: field.isPublished,
    sortOrder: field.sortOrder,
    value,
  });
}

export default function FooterSocialsManager({
  embedded = false,
  sectionId: sectionIdProp = null,
  pageId: pageIdProp = null,
  pageName: pageNameProp = "",
  sectionName: sectionNameProp = "",
}) {
  const notify = useNotify();
  const admin = useFooterSectionAdmin({ skip: Boolean(sectionIdProp) });

  const sectionId = sectionIdProp ?? admin.sectionId;
  const pageId = pageIdProp ?? admin.pageId;
  const pageName = pageNameProp || admin.pageName;
  const sectionName = sectionNameProp || admin.sectionName;
  const sectionLoading = sectionIdProp ? false : admin.loading;
  const sectionError = sectionIdProp ? "" : admin.error;
  const reloadSection = admin.reload;

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadFields = useCallback(async () => {
    if (!sectionId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listFields(sectionId);
      const list = Array.isArray(data) ? data : [];
      setFields(list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    if (sectionId) loadFields();
  }, [sectionId, loadFields]);

  const socials = useMemo(() => groupSocialFields(fields), [fields]);

  async function handleUploadIcon(file) {
    if (!sectionId || !file) return "";
    setUploadBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mediaType", "image");
      formData.append("groupKey", "footer-socials");
      formData.append("altText", file.name);
      formData.append("isPrimary", "false");
      formData.append("sortOrder", "0");

      const uploaded = await uploadMedia(sectionId, formData);
      const url = uploaded?.url ?? uploaded?.Url ?? "";
      if (!url) throw new Error("Upload did not return a URL");
      notify.success("Icon uploaded");
      return url;
    } catch (err) {
      notify.error(getApiErrorMessage(err));
      return "";
    } finally {
      setUploadBusy(false);
    }
  }

  async function handleSubmit(form) {
    if (!sectionId) return;
    setBusy(true);
    try {
      if (editingGroup) {
        const updates = [
          { field: editingGroup.fields.platform, value: form.platform },
          { field: editingGroup.fields.label, value: form.label },
          { field: editingGroup.fields.url, value: form.url },
          { field: editingGroup.fields.icon, value: form.icon || "" },
        ];

        for (const item of updates) {
          if (!item.field) continue;
          await persistFieldValue(item.field, item.value);
        }

        if (!editingGroup.fields.icon) {
          const iconDef = socialFieldDefs(editingGroup.index).find(
            (def) => def.suffix === "icon"
          );
          if (iconDef) {
            await createField(sectionId, {
              key: iconDef.key,
              label: iconDef.label,
              fieldType: iconDef.fieldType,
              isRichText: false,
              isRequired: false,
              isPublished: true,
              sortOrder: iconDef.sortOrder,
              value: form.icon || "",
            });
          }
        }

        notify.success("Social link updated");
      } else {
        const index = nextSocialIndex(socials);
        const defs = socialFieldDefs(index);
        const values = {
          platform: form.platform,
          label: form.label,
          url: form.url,
          icon: form.icon || "",
        };

        for (const def of defs) {
          await createField(sectionId, {
            key: def.key,
            label: def.label,
            fieldType: def.fieldType,
            isRichText: false,
            isRequired: false,
            isPublished: true,
            sortOrder: def.sortOrder,
            value: values[def.suffix] ?? "",
          });
        }
        notify.success("Social link added");
      }

      setModalOpen(false);
      setEditingGroup(null);
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      const ids = Object.values(deleteTarget.fields)
        .filter(Boolean)
        .map((field) => field.id);

      for (const id of ids) {
        await deleteField(id);
      }

      notify.success("Social link deleted");
      setDeleteTarget(null);
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (sectionLoading || (sectionId && loading)) {
    return <LoadingState message="Loading footer socials…" />;
  }

  if (sectionError) {
    return <ErrorState message={sectionError} onRetry={reloadSection} />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={loadFields} />;
  }

  if (!sectionId) {
    return (
      <EmptyState
        title="Footer section missing"
        description="Restart the backend so the Global Layout footer seed can run."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {!embedded ? (
            <>
              <h2 className="text-2xl font-semibold text-slate-900">
                Footer socials
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Add, edit, delete links, and change icons (preset or upload).
                {pageName ? (
                  <>
                    {" "}
                    Source: <span className="font-medium">{pageName}</span>
                    {sectionName ? ` · ${sectionName}` : ""}
                  </>
                ) : null}
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-500">
              Use <span className="font-medium text-slate-700">Edit</span> to
              change the icon, or <span className="font-medium text-slate-700">Delete</span>{" "}
              to remove a social entirely.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingGroup(null);
            setModalOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white"
        >
          <IoAddOutline className="h-5 w-5" />
          Add social link
        </button>
      </div>

      {socials.length === 0 ? (
        <EmptyState
          title="No social links yet"
          description="Add Twitter, LinkedIn, Instagram, and other profiles for the footer."
          action={
            <button
              type="button"
              onClick={() => {
                setEditingGroup(null);
                setModalOpen(true);
              }}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Add social link
            </button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  Platform
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 md:table-cell">
                  URL
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {socials.map((social) => {
                const iconSrc = resolveSocialIconSrc(social);
                const option = SOCIAL_PLATFORM_OPTIONS.find(
                  (item) => item.value === String(social.platform).toLowerCase()
                );

                return (
                  <tr key={social.index} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100">
                          <img
                            src={iconSrc}
                            alt=""
                            className="h-4 w-4 object-contain"
                          />
                        </span>
                        <div>
                          <p className="font-medium text-slate-900">
                            {social.label || option?.label || social.platform}
                          </p>
                          <p className="text-xs capitalize text-slate-500">
                            {social.icon
                              ? "Custom icon"
                              : social.platform || "custom"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden max-w-xs truncate px-4 py-3 text-slate-600 md:table-cell">
                      {social.url || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        {social.url ? (
                          <a
                            href={
                              /^https?:\/\//i.test(social.url)
                                ? social.url
                                : `https://${social.url}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                            aria-label={`Open ${social.label || social.platform}`}
                            title="Open link"
                          >
                            <IoOpenOutline className="h-4 w-4" />
                          </a>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroup(social);
                            setModalOpen(true);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          aria-label={`Edit ${social.label || social.platform}`}
                          title="Edit / change icon"
                        >
                          <IoCreateOutline className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(social)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-2 text-xs font-medium text-red-600 hover:bg-red-50"
                          aria-label={`Delete ${social.label || social.platform}`}
                          title="Delete social"
                        >
                          <IoTrashOutline className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!embedded && pageId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-600">
            Need to edit footer text or link columns too?
          </p>
          <Link
            to={`/cmsadmin/dashboard/pages/${pageId}/sections`}
            className="mt-2 inline-flex text-sm font-medium text-emerald-700 hover:text-emerald-800"
          >
            Open Global Layout sections
          </Link>
        </div>
      ) : null}

      <SocialFormModal
        open={modalOpen}
        initial={editingGroup ? socialGroupToForm(editingGroup) : null}
        busy={busy}
        uploadBusy={uploadBusy}
        onClose={() => {
          if (busy || uploadBusy) return;
          setModalOpen(false);
          setEditingGroup(null);
        }}
        onSubmit={handleSubmit}
        onUploadIcon={handleUploadIcon}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete social link?"
        message={
          deleteTarget
            ? `This will remove ${deleteTarget.label || deleteTarget.platform} from the footer.`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
